import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { HelpCircle, CheckCircle2, AlertTriangle, FlaskConical } from "lucide-react";
import { GLOSSARY } from "../lib/constants";

export const Helper = ({ children }) => (
  <p className="text-xs text-[#475569] mt-1 leading-snug">{children}</p>
);

export const Tip = ({ text, example }) => (
  <Popover>
    <PopoverTrigger asChild>
      <button type="button" aria-label="More info" className="inline-flex align-middle ml-1.5 text-[#475569] hover:text-[#059669] transition-colors" data-testid="field-tooltip-trigger">
        <HelpCircle size={14} />
      </button>
    </PopoverTrigger>
    <PopoverContent className="max-w-xs text-sm bg-[#0B1F3A] text-[#F5F8FF] border-none shadow-xl">
      <p>{text}</p>
      {example && <p className="mt-2 text-xs text-[#059669]">Example: {example}</p>}
    </PopoverContent>
  </Popover>
);

export const Term = ({ children, term }) => {
  const def = GLOSSARY[term || children];
  if (!def) return <span>{children}</span>;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <span className="underline decoration-dotted decoration-[#059669] underline-offset-2 cursor-pointer hover:text-[#059669] transition-colors" data-testid={`glossary-term-${term || children}`}>
          {children}
        </span>
      </PopoverTrigger>
      <PopoverContent className="max-w-xs text-sm bg-[#0B1F3A] text-[#F5F8FF] border-none shadow-xl">
        <p className="font-semibold text-[#059669] mb-1">{term || children}</p>
        <p>{def}</p>
      </PopoverContent>
    </Popover>
  );
};

export const StatusChip = ({ status }) => {
  const config = {
    verified: {
      label: "Verified (Solarian)", icon: CheckCircle2,
      cls: "bg-[#0B1F3A] text-[#F5F8FF] border-[#0B1F3A]",
      tip: "Reviewed and confirmed by Solarian's regulatory experts",
    },
    conditional: {
      label: "Conditional — verify", icon: AlertTriangle,
      cls: "bg-amber-50 text-amber-800 border-amber-300",
      tip: "Applies only if the site condition is confirmed — verify before applying",
    },
    ai_draft: {
      label: "AI draft — expert review pending", icon: FlaskConical,
      cls: "bg-violet-50 text-violet-700 border-violet-300",
      tip: "Drafted by AI research — visible only to admin until expert approval",
    },
  }[status] || null;
  if (!config) return null;
  const Icon = config.icon;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" onClick={(e) => e.stopPropagation()} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium border cursor-pointer ${config.cls}`} data-testid={`status-chip-${status}`}>
          <Icon size={11} /> {config.label}
        </button>
      </PopoverTrigger>
      <PopoverContent className="max-w-xs text-sm bg-[#0B1F3A] text-[#F5F8FF] border-none shadow-xl">
        {config.tip}
      </PopoverContent>
    </Popover>
  );
};

export const EmptyState = ({ icon: Icon, title, subtitle, action }) => (
  <div className="flex flex-col items-center justify-center text-center py-16 px-6" data-testid="empty-state">
    {Icon && <Icon size={48} strokeWidth={1} className="text-[#0B1F3A]/30 mb-4" />}
    <h3 className="font-serif text-xl text-[#0B1F3A]">{title}</h3>
    {subtitle && <p className="text-sm text-[#475569] mt-2 max-w-sm">{subtitle}</p>}
    {action && <div className="mt-6">{action}</div>}
  </div>
);
